/**
 * W1-11b (app-shell-ui): the shell's React context and the hooks its components use to follow the services
 * (settings, editor store, transport, history, keymap, i18n, viewport). Everything is read through `services`, so
 * the components run against the real app and against `test/fakes` alike.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { AudioEngine, TransportState } from '../../audio/types';
import type { UserSettings } from '../settings/types';
import type { ChartDoc } from '../../core/model/types';
import type { DocStore, EditorStore, HistoryView, VersionTopic } from '../../state/types';
import { useExternalSelector } from '../../state/react';
import type { MessageKey } from '../../i18n/types';
import type { AppServices, WhenContext } from '../registry/types';
import type { ShellUiService, UiSnapshot } from '../uiService';
import { layoutViewport, type Viewport } from './LayoutPresets';

/** Region focus helpers the shell needs from FocusRegions (W1-11a). */
export interface ShellFocus {
  handBack(): boolean;
  focus(region: string): boolean;
}

export interface ShellEnv {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  /** Current editor context (keyboard router, else registry, else built from the services). */
  readonly when: () => WhenContext;
  /** Force a when-atom on/off (`paletteOpen`, `dialog`), when a keyboard router is installed. */
  readonly setAtom: (atom: string, on: boolean) => void;
  readonly focus: ShellFocus | null;
}

const ShellContext = createContext<ShellEnv | null>(null);
ShellContext.displayName = 'Shell';

export function ShellProvider(props: { readonly env: ShellEnv; readonly children: ReactNode }) {
  return <ShellContext value={props.env}>{props.children}</ShellContext>;
}

export function useShell(): ShellEnv {
  const env = useContext(ShellContext);
  if (env === null) throw new Error('useShell: no ShellProvider above this component');
  return env;
}

export function useOptionalShell(): ShellEnv | null {
  return useContext(ShellContext);
}

/** Selects from the UI service's snapshot. */
export function useUi<T>(ui: ShellUiService, select: (s: UiSnapshot) => T): T {
  const subscribe = useCallback((cb: () => void) => ui.subscribe(cb), [ui]);
  const get = useCallback(() => select(ui.getSnapshot()), [ui, select]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The user settings (re-renders on every change). */
export function useSettingsValue(services: Pick<AppServices, 'settings'>): UserSettings {
  const { settings } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => settings.subscribe(cb), [settings]);
  const get = useCallback((): UserSettings => settings.value, [settings]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Selects from the editor store (contract EditorStoreApi), re-rendering only when the selection changes. */
export function useEditorState<T>(
  services: Pick<AppServices, 'editor'>,
  select: (s: EditorStore) => T,
  equal?: (a: T, b: T) => boolean,
): T {
  const { editor } = services;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      editor.subscribe(() => {
        cb();
      }),
    [editor],
  );
  const get = useCallback(() => editor.getState(), [editor]);
  return useExternalSelector(editor, subscribe, get, select, equal);
}

/** Re-renders when the services swap their store / engine / timeline (project open and close). */
export function useServicesChange(services: Pick<AppServices, 'onChange'>): number {
  const [version, setVersion] = useState(0);
  useEffect(
    () =>
      services.onChange(() => {
        setVersion((v) => v + 1);
      }),
    [services],
  );
  return version;
}

/** The open project's DocStore, or null (follows project open / close). */
export function useStore(services: AppServices): DocStore | null {
  const subscribe = useCallback((cb: () => void) => services.onChange(cb), [services]);
  const get = useCallback(() => services.store, [services]);
  return useSyncExternalStore(subscribe, get, get);
}

const NO_STORE = {};
const noop = (): void => undefined;

/**
 * Selects from the open project's document; re-selects only when one of `topics` changed (version counters), and
 * returns `fallback` without a project.
 */
export function useDocSelect<T>(
  store: DocStore | null,
  topics: readonly VersionTopic[],
  select: (doc: ChartDoc) => T,
  fallback: T,
  equal?: (a: T, b: T) => boolean,
): T {
  const topicKey = topics.join('|');
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      store === null
        ? noop
        : store.subscribe(() => {
            cb();
          }),
    [store],
  );
  const getKey = useCallback(
    (): string =>
      store === null
        ? ''
        : topicKey
            .split('|')
            .map((t) => String(store.version(t as VersionTopic)))
            .join('.'),
    [store, topicKey],
  );
  return useExternalSelector(
    store ?? NO_STORE,
    subscribe,
    getKey,
    () => (store === null ? fallback : select(store.doc)),
    equal,
  );
}

/** The transport state (playing, rate, loop...). */
export function useTransportState(audio: AudioEngine): TransportState {
  const subscribe = useCallback((cb: () => void): Unsubscribe => audio.transport.onChange(cb), [audio]);
  const get = useCallback(() => audio.transport.state, [audio]);
  return useSyncExternalStore(subscribe, get, get);
}

export interface HistorySummary {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: MessageRef | null;
  readonly redoLabel: MessageRef | null;
}

const NO_HISTORY: HistorySummary = { canUndo: false, canRedo: false, undoLabel: null, redoLabel: null };

function summarize(h: HistoryView): HistorySummary {
  const entries = h.entries();
  return {
    canUndo: h.canUndo,
    canRedo: h.canRedo,
    undoLabel: entries[h.position]?.label ?? null,
    redoLabel: entries[h.position + 1]?.label ?? null,
  };
}

function sameSummary(a: HistorySummary, b: HistorySummary): boolean {
  return (
    a.canUndo === b.canUndo && a.canRedo === b.canRedo && a.undoLabel === b.undoLabel && a.redoLabel === b.redoLabel
  );
}

function historyKey(h: HistoryView): string {
  const entries = h.entries();
  const label = (i: number): string => {
    const l = entries[i]?.label;
    return l === undefined ? '' : `${l.key}${l.params === undefined ? '' : JSON.stringify(l.params)}`;
  };
  return `${String(h.position)}|${String(entries.length)}|${String(h.canUndo)}|${String(h.canRedo)}|${label(h.position)}|${label(h.position + 1)}`;
}

/** Undo / redo availability and the labels of the next steps ("Undo: Move 12 notes"). */
export function useHistorySummary(store: DocStore | null): HistorySummary {
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => {
      if (store === null) return noop;
      const offHistory = store.history.subscribe(cb);
      const offCommits = store.subscribeCommits(() => {
        cb();
      });
      return () => {
        offHistory();
        offCommits();
      };
    },
    [store],
  );
  const getKey = useCallback((): string => (store === null ? '' : historyKey(store.history)), [store]);
  return useExternalSelector(
    store ?? NO_STORE,
    subscribe,
    getKey,
    () => (store === null ? NO_HISTORY : summarize(store.history)),
    sameSummary,
  );
}

/** A number that changes whenever the keymap changes (preset, overrides, modules). */
export function useKeymapVersion(services: Pick<AppServices, 'keymap'>): number {
  const { keymap } = services;
  const [version, setVersion] = useState(0);
  useEffect(
    () =>
      keymap.subscribe(() => {
        setVersion((v) => v + 1);
      }),
    [keymap],
  );
  return version;
}

export interface ShellText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`; a new object per locale so memoised children update. */
export function useText(services: Pick<AppServices, 'i18n'>): ShellText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

function readViewport(win: Window | undefined): Viewport {
  return win === undefined ? { width: 1536, height: 730 } : { width: win.innerWidth, height: win.innerHeight };
}

/** Window size in CSS px (re-renders on resize). */
export function useWindowSize(): Viewport {
  const win = typeof window === 'undefined' ? undefined : window;
  const [size, setSize] = useState<Viewport>(() => readViewport(win));
  useEffect(() => {
    if (win === undefined) return undefined;
    let frame = 0;
    const onResize = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = readViewport(win);
        setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
      });
    };
    // Apply synchronously too, so a resize is reflected without waiting for a frame (tests, zoom changes).
    const onResizeNow = (): void => {
      const next = readViewport(win);
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
      onResize();
    };
    win.addEventListener('resize', onResizeNow);
    return () => {
      cancelAnimationFrame(frame);
      win.removeEventListener('resize', onResizeNow);
    };
  }, [win]);
  return size;
}

/** Viewport in layout px (window size divided by the UI scale setting). */
export function useLayoutViewport(services: Pick<AppServices, 'settings'>): Viewport {
  const size = useWindowSize();
  const scale = useSettingsValue(services).general.uiScale;
  return useMemo(() => layoutViewport(size, scale), [size, scale]);
}

/**
 * Marks values a memoised computation follows although it reads them through services (version counters bumped by
 * subscriptions): `useMemo(() => { dependsOn(version); return compute(); }, [version])`.
 */
export function dependsOn(...values: readonly unknown[]): number {
  return values.length;
}

/** Message key helper for components: a MessageRef from a key and params. */
export function msg(key: MessageKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}
